import React, {useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Snackbar from '@mui/material/Snackbar';

import {deleteLesson} from '../../redux/slices/builder';
import {selectUserID} from '../../redux/slices/auth';
import {selectUserGroup} from '../../redux/slices/user';
import ShareDialog from './ShareDialog';
import AreYouSureDialog from './AreYouSureDialog';
import {FEATURES, SNACKBAR_DURATION} from '../../constants';
import {faLink, faPencil, faTrashAlt, faCopy, faFileInvoice} from '@fortawesome/pro-light-svg-icons';

export default function LessonMenu({
  anchorEl,
  isMenuOpen,
  toggleMenu,
  lesson,
  toggleCopyLessonDialog,
  isEditVisible = false,
}) {
  const [isAreYouSureDialogOpen, toggleAreYouSureDialog] = useState(false);
  const [isSnackbarOpen, toggleSnackbar] = useState(false);
  const [isShareDialogOpen, toggleShareDialog] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();

  const userID = useSelector(selectUserID);
  const userGroup = useSelector(selectUserGroup);

  const {_id: lessonID, userID: lessonUserID} = lesson || {};
  const isMyLesson = userID === lessonUserID;
  const shareURL = `${window.location.origin}/lesson/${lessonID}`;
  const {
    tier: {features},
  } = userGroup;
  const isContentProducerGroup = features.includes(FEATURES.VISIBILITY_PUBLIC);

  function handleEditLesson() {
    toggleMenu(false);
    history.push(`/builder/lessons/${lessonID}`);
  }

  function handleClickCopyLesson() {
    toggleMenu(false);
    toggleCopyLessonDialog(true);
  }

  function handleClickPrintWorksheet() {
    toggleMenu(false);
    window.open(`/builder/lessons/${lessonID}/worksheet`);
  }

  function handleClickDeleteLesson() {
    toggleMenu(false);
    toggleAreYouSureDialog(true);
  }

  async function handleDeleteLesson() {
    toggleAreYouSureDialog(false);
    await dispatch(deleteLesson(lessonID));
    history.push('/builder/lessons');
    toggleSnackbar(true);
  }

  function handleClickGetShareLink() {
    toggleMenu(false);
    toggleShareDialog(true);
  }

  return (
    <>
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isSnackbarOpen}
        autoHideDuration={SNACKBAR_DURATION}
        onClose={() => toggleSnackbar(false)}
        message="Lesson deleted"
      />
      <AreYouSureDialog
        isOpen={isAreYouSureDialogOpen}
        handleClose={() => toggleAreYouSureDialog(false)}
        handleClickActionButton={handleDeleteLesson}
        title="Delete lesson"
        message={`Delete "${lesson?.name}"?`}
        actionButtonText="DELETE"
      />
      <ShareDialog open={isShareDialogOpen} handleClose={() => toggleShareDialog(false)} shareURL={shareURL} />
      <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={() => toggleMenu(false)}>
        <MenuItem onClick={handleClickGetShareLink}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faLink} />
          </ListItemIcon>
          <ListItemText primary="Get Link" />
        </MenuItem>

        {!isMyLesson && !isContentProducerGroup && (
          <MenuItem onClick={handleClickCopyLesson}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faCopy} />
            </ListItemIcon>
            <ListItemText primary="Make It Mine" />
          </MenuItem>
        )}

        <MenuItem onClick={handleClickPrintWorksheet}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faFileInvoice} />
          </ListItemIcon>
          <ListItemText primary="Print Worksheet" />
        </MenuItem>

        {(isMyLesson || isContentProducerGroup) && isEditVisible && (
          <MenuItem onClick={handleEditLesson}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faPencil} />
            </ListItemIcon>
            <ListItemText primary="Edit" />
          </MenuItem>
        )}

        {isMyLesson && (
          <MenuItem onClick={handleClickDeleteLesson}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faTrashAlt} className="clr-danger-400" />
            </ListItemIcon>
            <ListItemText primary="Delete" className="clr-danger-400" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
